/** Oturum bağlamı: me, giriş/çıkış. Hesap değişince sorgu önbelleği temizlenir (T22 taslak izolasyonu). */
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { setUnauthenticatedHandler } from '../api/client';
import * as ep from '../api/endpoints';
import type { Me } from '../api/types';
import { ApiError } from '../lib/errors';
import { detachPush } from '../lib/push';

interface AuthState {
  me: Me | null;
  loading: boolean;
  /** Sunucuya ulaşılamıyor (ör. güncelleme sırasında). Oturum düşmüş sayılmaz; giriş ekranı gösterilmez. */
  offline: boolean;
  isAdmin: boolean;
  signIn(username: string, password: string): Promise<void>;
  /** İlk giriş / sıfırlama sonrası: e-posta + kod + yeni şifre; başarılıysa oturum açılır. */
  firstLogin(email: string, code: string, newPassword: string): Promise<void>;
  signOut(): Promise<void>;
  reload(): Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);

  // Oturum yalnızca sunucu "oturum yok" dediğinde düşer. Bağlantı hatası ya da sunucu yeniden başlarken gelen
  // 5xx yanıtında kısa aralıklarla yeniden denenir; kullanıcı boşuna giriş ekranına atılmaz.
  const reload = useCallback(async () => {
    setOffline(false);
    for (let attempt = 0; ; attempt++) {
      try {
        setMe(await ep.me());
        break;
      } catch (e) {
        const authError = e instanceof ApiError && e.status >= 400 && e.status < 500;
        if (authError) { setMe(null); break; }
        if (attempt >= 5) { setOffline(true); break; }
        await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** attempt, 8000)));
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    setUnauthenticatedHandler(() => { setMe(null); qc.clear(); });
    void reload();
  }, [qc, reload]);

  const signIn = useCallback(async (username: string, password: string) => {
    const res = await ep.login(username, password);
    qc.clear();
    setMe(res);
  }, [qc]);

  const firstLogin = useCallback(async (email: string, code: string, newPassword: string) => {
    const res = await ep.firstLogin(email, code, newPassword);
    qc.clear();
    setMe(res);
  }, [qc]);

  const signOut = useCallback(async () => {
    await detachPush(); // oturum kapanmadan: cihaz kaydı bu kullanıcıya aittir
    try { await ep.logout(); } catch { /* oturum zaten düşmüş olabilir */ }
    qc.clear();
    setMe(null);
  }, [qc]);

  const value = useMemo<AuthState>(
    () => ({ me, loading, offline, isAdmin: me?.role === 'admin', signIn, firstLogin, signOut, reload }),
    [me, loading, offline, signIn, firstLogin, signOut, reload],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('AuthProvider eksik');
  return v;
}

export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error('Oturum yok');
  return me;
}
